import { useRef, useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { RotateCcw, Volume2, VolumeX } from "lucide-react";

// [open, high, low, close] — SOL's journey: the crash, the $8 bottom, the vindication
const CANDLES = [
  [175, 182, 148, 152], [152, 158, 120, 126], [126, 140, 122, 136], [136, 138, 102, 108],
  [108, 112, 82, 88], [88, 99, 83, 95], [95, 96, 60, 66], [66, 70, 44, 48],
  [48, 52, 29, 34], [34, 36, 18, 22], [22, 24, 9, 12], [12, 14, 7, 8],
  [8, 12, 7, 10], [10, 13, 8, 9], [9, 15, 8, 14],
  [14, 22, 13, 20], [20, 23, 15, 17], [17, 28, 16, 26], [26, 40, 24, 38], [38, 42, 31, 33],
  [33, 52, 32, 50], [50, 72, 48, 68], [68, 74, 56, 60], [60, 92, 58, 88], [88, 116, 86, 110],
  [110, 118, 93, 98], [98, 138, 96, 132], [132, 164, 128, 158], [158, 166, 139, 146],
  [146, 188, 142, 182], [182, 220, 178, 214],
];
const N = CANDLES.length;
const STEP = 19.4;
const priceY = (p) => 252 - (p / 230) * 240;
const candleX = (i) => 14 + i * STEP;
const GREEN = "#14F195";
const RED = "#ff4d67";

const X_TICKS = [
  [0, "JAN '22"], [5, "JUN '22"], [11, "DEC '22"], [17, "JUN '23"], [23, "DEC '23"], [29, "JUN '24"],
];

export const ChartReplay = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-120px" });
  const [runId, setRunId] = useState(0);
  const [idx, setIdx] = useState(0);
  const [soundOn, setSoundOn] = useState(true);
  const soundOnRef = useRef(true);
  const audioRef = useRef(null);

  const playTick = (close, up, final) => {
    if (!soundOnRef.current) return;
    try {
      if (!audioRef.current) {
        audioRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const t = ctx.currentTime;
      const blip = (freq, delay, vol, type) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = type;
        osc.frequency.setValueAtTime(freq, t + delay);
        osc.frequency.exponentialRampToValueAtTime(freq * (up ? 1.4 : 0.72), t + delay + 0.07);
        gain.gain.setValueAtTime(0.0001, t + delay);
        gain.gain.exponentialRampToValueAtTime(vol, t + delay + 0.012);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + delay + 0.13);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t + delay);
        osc.stop(t + delay + 0.15);
      };
      const base = 240 + (close / 220) * 640;
      blip(base, 0, 0.1, up ? "sine" : "triangle");
      if (final) {
        blip(base * 1.25, 0.12, 0.12, "sine");
        blip(base * 1.5, 0.24, 0.14, "sine");
      }
    } catch {
      /* audio blocked until user interacts — fine */
    }
  };

  useEffect(() => {
    if (!inView) return;
    setIdx(0);
    let i = 0;
    const t = setInterval(() => {
      i += 1;
      if (i > N) {
        clearInterval(t);
        return;
      }
      const [o, , , c] = CANDLES[i - 1];
      playTick(c, c >= o, i === N);
      setIdx(i);
    }, 4000 / N);
    return () => clearInterval(t);
  }, [inView, runId]);

  const done = idx >= N;
  const price = idx === 0 ? CANDLES[0][0] : CANDLES[Math.min(idx, N) - 1][3];
  const lastUp = idx === 0 ? true : CANDLES[Math.min(idx, N) - 1][3] >= CANDLES[Math.min(idx, N) - 1][0];

  return (
    <section id="the-call" ref={ref} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20 sm:py-28">
      <div className="grid lg:grid-cols-[1fr_1.3fr] gap-10 items-center">
        <div>
          <p className="font-bebas tracking-[0.35em] text-[#14F195] text-lg">INTERACTIVE EVIDENCE</p>
          <h2 data-testid="chart-heading" className="font-marker text-4xl sm:text-5xl text-white mt-2 -rotate-1">
            REPLAY <span className="gradient-sol">THE CALL</span>
          </h2>
          <p className="text-zinc-300 leading-relaxed mt-5 max-w-md">
            This is what conviction looks like on a chart. From the FTX crater to the top of the
            timeline — watch the call play out in real time. No hindsight. No luck. Just Curb.
          </p>
          <div className="mt-8 flex items-end gap-5">
            <div>
              <p className="font-bebas tracking-[0.25em] text-zinc-500 text-sm">SOL PRICE</p>
              <p
                data-testid="chart-price-counter"
                className={`font-bebas text-6xl sm:text-7xl leading-none transition-colors duration-200 ${
                  lastUp ? "text-[#14F195] text-glow-green" : "text-[#ff4d67]"
                }`}
              >
                ${Math.round(price)}
              </p>
            </div>
            {done && (
              <motion.span
                initial={{ scale: 0, rotate: -20 }}
                animate={{ scale: 1, rotate: -6 }}
                transition={{ type: "spring", stiffness: 300 }}
                data-testid="chart-multiplier-badge"
                className="font-marker text-2xl text-[#050409] bg-[#14F195] px-3 py-1 rounded-lg shadow-[0_0_30px_rgba(20,241,149,0.5)]"
              >
                26x
              </motion.span>
            )}
          </div>
          <div className="mt-8 flex items-center gap-3">
            <button
              onClick={() => setRunId((n) => n + 1)}
              data-testid="chart-replay-button"
              className="btn-ghost-x rounded-full px-6 py-2.5 flex items-center gap-2 font-bebas tracking-widest text-lg"
            >
              <RotateCcw size={16} /> REPLAY THE CALL
            </button>
            <button
              onClick={() => {
                soundOnRef.current = !soundOnRef.current;
                setSoundOn(soundOnRef.current);
              }}
              data-testid="chart-sound-toggle"
              className="btn-ghost-x rounded-full p-3"
              title={soundOn ? "Mute candle sounds" : "Unmute candle sounds"}
              aria-label="Toggle candle sounds"
            >
              {soundOn ? <Volume2 size={16} className="text-[#14F195]" /> : <VolumeX size={16} className="text-zinc-500" />}
            </button>
          </div>
        </div>

        <div className="neon-card p-5 sm:p-8" data-testid="replay-candle-chart">
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="font-mono text-xs text-zinc-500">SOL/USD · 1M · THE CURB CYCLE</span>
            <span className="font-mono text-xs text-zinc-600">
              O <span className="text-zinc-400">{idx > 0 ? CANDLES[Math.min(idx, N) - 1][0] : "—"}</span>{" "}
              H <span className="text-zinc-400">{idx > 0 ? CANDLES[Math.min(idx, N) - 1][1] : "—"}</span>{" "}
              L <span className="text-zinc-400">{idx > 0 ? CANDLES[Math.min(idx, N) - 1][2] : "—"}</span>{" "}
              C <span style={{ color: lastUp ? GREEN : RED }}>{idx > 0 ? CANDLES[Math.min(idx, N) - 1][3] : "—"}</span>
            </span>
          </div>
          <svg viewBox="0 0 680 302" className="w-full" aria-label="SOL candlestick chart from $8 to $214">
            {[8, 50, 100, 150, 200].map((p) => (
              <g key={p}>
                <line x1="10" x2="612" y1={priceY(p)} y2={priceY(p)} stroke="#9945FF" strokeOpacity="0.14" strokeDasharray="3 7" />
                <text x="622" y={priceY(p) + 4} fill="#71717a" fontFamily="monospace" fontSize="12">{`$${p}`}</text>
              </g>
            ))}
            <text x="14" y="24" fill="#71717a" fontFamily="Bebas Neue" fontSize="14" letterSpacing="2">
              "SOLANA IS DEAD" ERA
            </text>

            {CANDLES.slice(0, idx).map(([o, h, l, c], i) => {
              const up = c >= o;
              const x = candleX(i);
              const bodyTop = priceY(Math.max(o, c));
              const bodyH = Math.max(2, Math.abs(priceY(o) - priceY(c)));
              const color = up ? GREEN : RED;
              return (
                <g key={i}>
                  <line x1={x + 6} x2={x + 6} y1={priceY(h)} y2={priceY(l)} stroke={color} strokeWidth="1.5" />
                  <rect x={x} y={bodyTop} width="12" height={bodyH} fill={color} stroke={color} strokeWidth="1.5" rx="1" />
                </g>
              );
            })}

            {idx > 11 && (
              <g>
                <line
                  x1={candleX(11) + 6}
                  x2={candleX(11) + 6}
                  y1={priceY(7) + 7}
                  y2="264"
                  stroke={GREEN}
                  strokeOpacity="0.35"
                  strokeDasharray="2 5"
                />
                <circle cx={candleX(11) + 6} cy={priceY(7)} r="6" fill="none" stroke={GREEN} strokeWidth="2">
                  <animate attributeName="r" values="5;9;5" dur="1.6s" repeatCount="indefinite" />
                </circle>
                <rect x={candleX(12) + 4} y="236" width="206" height="21" rx="7" fill="#0a0714" stroke={GREEN} strokeOpacity="0.55" strokeWidth="1" />
                <text x={candleX(12) + 14} y="251" fill={GREEN} fontFamily="Permanent Marker" fontSize="13">
                  $8 — "the generational buy"
                </text>
              </g>
            )}
            {done && (
              <g>
                <line x1="10" x2="612" y1={priceY(214)} y2={priceY(214)} stroke={GREEN} strokeOpacity="0.5" strokeDasharray="5 4" />
                <rect x="614" y={priceY(214) - 10} width="58" height="20" rx="4" fill={GREEN} />
                <text x="620" y={priceY(214) + 4} fill="#050409" fontFamily="monospace" fontSize="12" fontWeight="bold">
                  $214
                </text>
              </g>
            )}

            {/* time axis */}
            <line x1="10" x2="612" y1="264" y2="264" stroke="#3f3f46" strokeWidth="1" />
            {X_TICKS.map(([i, label]) => (
              <g key={label}>
                <line x1={candleX(i) + 6} x2={candleX(i) + 6} y1="264" y2="270" stroke="#3f3f46" strokeWidth="1" />
                <text
                  x={candleX(i) + 6}
                  y="284"
                  textAnchor="middle"
                  fill={label === "DEC '22" ? GREEN : "#71717a"}
                  fontFamily="monospace"
                  fontSize="11"
                  letterSpacing="0.5"
                >
                  {label}
                </text>
              </g>
            ))}
            <text x="612" y="298" textAnchor="end" fill="#52525b" fontFamily="monospace" fontSize="10">
              THE 18-MONTH VINDICATION ARC · +2,575% FROM THE CALL
            </text>
          </svg>
          <div className="flex items-center justify-between mt-3 px-1 flex-wrap gap-2">
            <div className="flex items-center gap-4 font-mono text-[11px] text-zinc-500">
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-2.5 h-2.5 rounded-[2px]" style={{ background: GREEN }} /> BULL CANDLE
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-2.5 h-2.5 rounded-[2px]" style={{ background: RED }} /> BEAR CANDLE
              </span>
            </div>
            <span className="font-mono text-[11px] text-zinc-600">SOURCE: THE TIMELINE. TRUST ME BRO.</span>
          </div>
        </div>
      </div>
    </section>
  );
};
